/**
 * The event fold — one implementation, used by both sides.
 *
 * The server's `engine/apply.ts` folds a log to replay a round; the client's
 * `feed/feed.ts` folds live frames to keep the kitchen current between the
 * two-second keyframes. Those two are the same operation, so they are the same
 * code. Writing it twice is how the "replay matches what the players saw"
 * guarantee quietly becomes false.
 *
 * It operates on `SerialisedState` because that is what a KEYFRAME carries and
 * what the client holds. It is pure: the input is never mutated.
 *
 * Only *types* are imported from `./index`, so there is no runtime cycle.
 */

import type {
  Carryable,
  Cell,
  Container,
  GameEvent,
  IngredientId,
  PlayerId,
  SerialisedState,
  Station,
} from './index'
import { isCookingEquipment } from './index'

const sameCell = (a: Cell, b: Cell): boolean => a.x === b.x && a.y === b.y

function clone<T>(value: T): T {
  return structuredClone(value)
}

function stationAt(state: SerialisedState, cell: Cell): Station | undefined {
  return state.stations.find((s) => sameCell(s.cell, cell))
}

function playerAt(state: SerialisedState, id: PlayerId) {
  return state.players.find((p) => p.id === id)
}

/**
 * Which slot a station keeps a given item in.
 *
 * Cooking equipment keeps a container in its own `container` slot, because
 * cooking reads that slot and nothing else; every other station keeps whatever
 * it holds in `item`. One rule, applied identically by the engine and by the
 * fold, so a `PUT_DOWN` needs no extra event to say where the thing landed.
 */
export function containerOnStation(station: Station): Container | null {
  if (isCookingEquipment(station.type)) return station.container
  return station.item && station.item.type === 'container' ? station.item.container : null
}

/**
 * Find a container anywhere in the kitchen by id — on a station, or in
 * somebody's hands. `COOK_STATE_CHANGED` addresses it this way because a
 * container is no longer welded to a cell.
 */
export function findContainer(state: SerialisedState, id: string): Container | null {
  for (const station of state.stations) {
    if (station.container && station.container.id === id) return station.container
    if (station.item?.type === 'container' && station.item.container.id === id) {
      return station.item.container
    }
  }
  for (const player of state.players) {
    if (player.holding?.type === 'container' && player.holding.container.id === id) {
      return player.holding.container
    }
  }
  return null
}

/**
 * A field that a keyframe recorded before it existed does not have.
 *
 * The fold is run against *stored* logs, years-old ones in principle, and
 * `scripts/assert-stored-session.mjs` compares a fold of a log to a keyframe out
 * of that same log with `JSON.stringify`. Writing a new field onto a station
 * that never had one therefore turns "the replay shows what the players saw"
 * into a failure for every tape recorded before today. So a field the tape does
 * not know about is left absent rather than being introduced: an old log folds
 * byte-for-byte as it always did, and a log recorded by a server that carries
 * the field keeps it maintained.
 */
function setIfPresent<K extends keyof Station>(station: Station, key: K, value: Station[K]): void {
  if (station[key] === undefined) return
  station[key] = value
}

/** Everything a hold leaves behind, cleared in one place. */
function clearProgress(station: Station): void {
  station.progressTicks = 0
  station.progressTargetTicks = 0
  station.resumedAtTick = null
  station.heldBy = null
  setIfPresent(station, 'heldSubject', null)
}

/**
 * Fold one event into a snapshot. Unknown or cue-only events return an
 * otherwise identical snapshot with the tick advanced, which is what makes
 * `events.reduce(applyToSnapshot, initial)` total.
 */
export function applyToSnapshot(state: SerialisedState, event: GameEvent): SerialisedState {
  if (event.type === 'KEYFRAME') {
    const restored = clone(event.state)
    restored.tick = Math.max(state.tick, event.tick + 1)
    return restored
  }

  // An observation, not a simulation. The tape carries what the player pressed
  // so a reviewer can see it; the fold must not move a single field because of
  // it — including `tick`, which is why this returns before the clone rather
  // than falling through to the no-op cases below. A frame carrying nothing but
  // input would otherwise advance the clock in the replay and not in the game.
  if (event.type === 'INPUT') return state

  // The same contract, one level up. `UTTERANCE` is what a chef said and `PLAN`
  // is what a planner decided; neither is a thing that happened in the kitchen,
  // and the kitchen is what this function folds. They return here rather than
  // falling through to the no-op cases below for exactly the reason `INPUT`
  // does: a seat talking on a tick the engine had nothing to say about produces
  // a frame carrying nothing else, and advancing `tick` for it would move the
  // replay's clock without moving the game's.
  if (event.type === 'UTTERANCE' || event.type === 'PLAN') return state

  // A seat's voice link is not the kitchen either, and is declined the same
  // way: a frame carrying nothing but a voice change must not move the clock.
  if (event.type === 'VOICE_STATE') return state

  // A cue from before the round, for the same reason: `COUNTDOWN` is emitted
  // while the room is still a lobby, so folding it would advance a clock that
  // has not started. It never reaches a log — the trace is created by
  // `startRound`, after the last of these has gone out.
  if (event.type === 'COUNTDOWN') return state

  const next = clone(state)
  next.tick = Math.max(next.tick, event.tick + 1)

  switch (event.type) {
    // -- movement and presence -------------------------------------------

    case 'PLAYER_MOVED': {
      const player = playerAt(next, event.playerId)
      if (player) {
        player.cell = { ...event.to }
        player.facing = event.facing
      }
      break
    }

    case 'PLAYER_TURNED': {
      const player = playerAt(next, event.playerId)
      if (player) player.facing = event.facing
      break
    }

    case 'PLAYER_CONNECTED':
    case 'PLAYER_DISCONNECTED': {
      const player = playerAt(next, event.playerId)
      if (player) player.connected = event.type === 'PLAYER_CONNECTED'
      break
    }

    // -- carrying ----------------------------------------------------------

    case 'PICKED_UP': {
      const player = playerAt(next, event.playerId)
      const station = stationAt(next, event.cell)
      if (player) player.holding = clone(event.item)
      if (station) {
        // The two plate stations hand out a copy and keep a count; every other
        // station hands over the one thing it was holding — from whichever of
        // its two slots that thing was in.
        if (station.type === 'plate_stack') station.cleanPlates = Math.max(0, station.cleanPlates - 1)
        else if (station.type === 'plate_return') station.dirtyPlates = Math.max(0, station.dirtyPlates - 1)
        else if (station.container && event.item.type === 'container' && station.container.id === event.item.container.id) {
          station.container = null
        } else station.item = null
        clearProgress(station)
      }
      break
    }

    case 'PUT_DOWN': {
      const player = playerAt(next, event.playerId)
      const station = stationAt(next, event.cell)
      if (player) player.holding = null
      if (station) {
        // The clean stack takes a plate back as a count, mirroring the way it
        // hands one out: a plate on the shelf is a plate on the shelf, and the
        // stack drawing is a height rather than an object.
        if (station.type === 'plate_stack') station.cleanPlates += 1
        // The sink keeps its washing-up as a pile, the way the plate return does.
        else if (station.type === 'sink') station.dirtyPlates += 1
        else if (event.item.type === 'container' && isCookingEquipment(station.type)) {
          station.container = clone(event.item.container)
        } else {
          station.item = clone(event.item)
        }
      }
      break
    }

    case 'TRASHED': {
      const player = playerAt(next, event.playerId)
      if (player && player.holding) {
        // The bin "clears contents of a container" — a loaded plate keeps its
        // identity and loses its food. Everything else in the hand is destroyed
        // outright, so that "my hands are full and there is nowhere to put
        // this" is never a state a player is stuck in. An empty plate only
        // reaches here when no plate station had room for it; the ordinary case
        // is a `PLATE_STOWED`. A pan is kept for the same reason a loaded plate
        // is — the burnt food it arrived with was already tipped out by the
        // `COOK_STATE_CHANGED` just before this event, and what is left is a
        // piece of the kitchen's equipment rather than rubbish.
        if (player.holding.type === 'container') {
          // nothing to do: the pan stays in hand, already emptied
        } else if (player.holding.type === 'plate' && player.holding.plate.contents.length > 0) {
          player.holding.plate.contents = []
        } else player.holding = null
      }
      break
    }

    case 'PLATE_STOWED': {
      const player = playerAt(next, event.playerId)
      const station = stationAt(next, event.cell)
      if (player) player.holding = null
      if (station) {
        if (station.type === 'plate_stack') station.cleanPlates += 1
        else if (station.type === 'plate_return') station.dirtyPlates += 1
      }
      break
    }

    case 'EQUIPMENT_RACKED': {
      const player = playerAt(next, event.playerId)
      const station = stationAt(next, event.cell)
      if (player) player.holding = null
      if (station) station.item = { type: 'equipment', id: event.equipmentId }
      break
    }

    // -- held progress -----------------------------------------------------

    case 'CHOP_PROGRESS':
    case 'WASH_PROGRESS':
    case 'EXTINGUISH_PROGRESS': {
      const station = stationAt(next, event.cell)
      if (station) {
        station.progressTicks = event.progressTicks
        station.progressTargetTicks = event.targetTicks
        station.resumedAtTick = event.resumedAtTick
        station.heldBy = event.resumedAtTick === null ? null : event.playerId
        // What the progress belongs to, derived rather than carried: the only
        // hold with a subject is a wash, its subject is the plate in the
        // washer's hands, and the fold already knows what everybody is holding.
        // Deriving it keeps the event — and therefore every stored `log.jsonl`
        // and the session-log schema — exactly as it was. A *pause* names no
        // holder, so it leaves the subject where it is: that is what lets the
        // same plate's wash resume and stops another plate inheriting it.
        if (event.resumedAtTick !== null) {
          // A wash with empty hands is a wash of the sink's pile, whose subject
          // is the engine's `SINK_PILE_SUBJECT` in `interact.ts`.
          const holder = playerAt(next, event.playerId)
          setIfPresent(
            station,
            'heldSubject',
            event.type !== 'WASH_PROGRESS'
              ? null
              : holder?.holding?.type === 'plate'
                ? holder.holding.plate.id
                : holder && holder.holding === null
                  ? 'sink-pile'
                  : null,
          )
        }
      }
      break
    }

    case 'CHOPPED': {
      const station = stationAt(next, event.cell)
      if (station) {
        station.item = { type: 'ingredient', id: event.to }
        clearProgress(station)
      }
      break
    }

    case 'WASHED': {
      const station = stationAt(next, event.cell)
      const player = playerAt(next, event.playerId)
      if (player?.holding?.type === 'plate' && player.holding.plate.id === event.plateId) {
        player.holding.plate.clean = true
      } else if (player && player.holding === null && station?.type === 'sink' && station.dirtyPlates > 0) {
        // Washed off the top of the sink's pile: one fewer dirty plate there,
        // and the clean one is in the washer's hands.
        station.dirtyPlates -= 1
        player.holding = { type: 'plate', plate: { id: event.plateId, clean: true, contents: [] } }
      }
      if (station) clearProgress(station)
      break
    }

    // -- the pan -----------------------------------------------------------

    // The fill and the empty move the *player's* hands; the pan itself always
    // arrives as the COOK_STATE_CHANGED that follows, so pan state has exactly
    // one writer.
    case 'PAN_FILLED': {
      const player = playerAt(next, event.playerId)
      if (player) player.holding = null
      break
    }

    case 'PAN_EMPTIED':
      break

    case 'COOK_STATE_CHANGED': {
      // Located by id rather than by cell: the container it describes may have
      // been picked up between the fill and the state change.
      const container = findContainer(next, event.containerId)
      if (container) {
        container.contents = [...event.contents]
        container.cookState = event.state
        container.phaseStartedAtTick = event.startedAtTick
        container.phaseEndsAtTick = event.endsAtTick
      }
      break
    }

    case 'PLATED': {
      const player = playerAt(next, event.playerId)
      if (player?.holding?.type === 'plate' && player.holding.plate.id === event.plateId) {
        player.holding.plate.contents = [...event.contents]
      }
      break
    }

    // -- serving and the plate cycle ---------------------------------------

    case 'SERVED': {
      const player = playerAt(next, event.playerId)
      if (player) player.holding = null
      if (event.matchedOrderId) {
        next.orders = next.orders.filter((o) => o.id !== event.matchedOrderId)
      }
      next.score += event.scoreDelta
      next.pendingPlates = [
        ...next.pendingPlates,
        { plateId: event.plateId, dueAtTick: event.returnsAtTick },
      ]
      break
    }

    case 'PLATE_RETURNED': {
      const station = stationAt(next, event.cell)
      if (station) station.dirtyPlates += 1
      // The *first* match, not every match: plate ids are derived from a
      // station's count, so a plate put back on the stack and taken again can
      // carry an id that is already in flight. The engine's return loop drains
      // one entry per event, and this has to drain exactly as many.
      const index = next.pendingPlates.findIndex((p) => p.plateId === event.plateId)
      if (index >= 0) {
        next.pendingPlates = [
          ...next.pendingPlates.slice(0, index),
          ...next.pendingPlates.slice(index + 1),
        ]
      }
      break
    }

    // -- orders and the round ----------------------------------------------

    // -- fire, time constraint #4 -----------------------------------------

    case 'FIRE_STARTED': {
      const station = stationAt(next, event.cell)
      if (station) {
        station.onFire = true
        clearProgress(station)
      }
      break
    }

    case 'FIRE_EXTINGUISHED': {
      const station = stationAt(next, event.cell)
      if (station) {
        station.onFire = false
        clearProgress(station)
      }
      break
    }

    case 'ORDER_SPAWNED':
      next.orders = [...next.orders, { ...event.order }]
      break

    case 'ORDER_EXPIRED':
      next.orders = next.orders.filter((o) => o.id !== event.orderId)
      next.score += event.scoreDelta
      break

    case 'ROUND_ENDED':
      next.score = event.score
      break

    // -- cues: both clients render them, neither's state changes ------------

    case 'ROUND_STARTED':
    case 'SYNC_BEEP':
    case 'PLAYER_BLOCKED':
    case 'COLLISION':
    case 'CONTESTED':
    case 'ACTION_FAILED':
    case 'NETWORK_SAMPLE':
      break
  }

  return next
}

// ---------------------------------------------------------------------------
// Derived reads — shared so the renderer and any analysis tool agree
// ---------------------------------------------------------------------------

/**
 * Live held progress in ticks. `progressTicks` is frozen at the last release,
 * so anything currently held has to be added back in.
 */
export function heldProgressTicks(station: Station, atTick: number): number {
  if (station.resumedAtTick === null) return station.progressTicks
  return station.progressTicks + Math.max(0, atTick - station.resumedAtTick)
}

/** 0..1 through a chop or a wash, or `null` when there is nothing under way. */
export function heldProgressFraction(station: Station, atTick: number): number | null {
  if (station.progressTargetTicks <= 0) return null
  const ticks = heldProgressTicks(station, atTick)
  if (ticks <= 0) return null
  return Math.min(1, ticks / station.progressTargetTicks)
}

/** 0..1 through the current pan phase. `null` when there is nothing to show. */
export function phaseFraction(
  startedAtTick: number | null,
  endsAtTick: number | null,
  atTick: number,
): number | null {
  if (startedAtTick === null || endsAtTick === null) return null
  const span = endsAtTick - startedAtTick
  if (span <= 0) return 1
  return Math.min(1, Math.max(0, (atTick - startedAtTick) / span))
}

/**
 * What is on a plate or in a hand, flattened for display and for matching.
 *
 * A container reports what is inside it — the FSM of inclusion means a
 * container is a thing that holds ingredients, so "what is in your hands" and
 * "what would you be serving" are the same question one level down. Carried
 * equipment reports nothing: an extinguisher is not food.
 */
export function carryableContents(item: Carryable | null): IngredientId[] {
  if (!item) return []
  switch (item.type) {
    case 'plate':
      return [...item.plate.contents]
    case 'container':
      return [...item.container.contents]
    case 'equipment':
      return []
    default:
      return [item.id]
  }
}
